<script setup lang="ts">
    import { reactive, ref, computed, onMounted } from 'vue';
    import { uploadImageBase64, UploadedResourceResponse, AiDropdownOption } from '@cwd/AiTools/AiEditor.access';
    import { getGenerationExample, AiImageCategory, getImageCategories, getSentencePromptExample } from '@cwd/AiTools/AiEditorExamples.access';
    import Modal from '@shared/components/Modal.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import FormTextArea from '@shared/components/Form/FormTextArea.vue';
    import { setupShared } from '@cwd/AiTools/Shared';
    import Terms from '@cwd/AiTools/Terms.vue';
    import { useImageHistory } from '@cwd/AiTools/useImageHistory';
    import Collapse from '@shared/components/Collapse.vue';
    import Loading from '@shared/components/Loading.vue';
    import '@cwd/AiTools/aiTools.css';

    const sizeOptions = [
        { label: `${$Resources.AIAssistantModelSizeSquare} - 1024x1024`, value: '1024x1024' },
        { label: `${$Resources.AIAssistantModelSizePortrait} - 1024x1536`, value: '1024x1536' },
        { label: `${$Resources.AIAssistantModelSizeLandscape} - 1536x1024`, value: '1536x1024' },
    ];

    const qualityOptions = [
        { label: $Resources.AIAssistantModelQualityStandard, value: 'auto' },
        { label: $Resources.AIAssistantModelQualityHd, value: 'high' },
    ];

    const { shared, disabled, acceptTerms, handleUnsuccessfulResponse, showConsumption, getFormattedConsumption, updateConsumption, makeOptionsRequest } =
        setupShared();
    const { history, init: initHistory, add: addToHistory, remove: removeFromHistory } = useImageHistory();

    const historyBaseSet = new Set<string>();
    const historyOpen = ref(false);

    const newImages = computed(() => shared.resultOptions.filter((b) => !historyBaseSet.has(b)));
    const historyImages = computed(() => shared.resultOptions.filter((b) => historyBaseSet.has(b)));
    const isGenerating = computed(() => shared.loading && shared.command === 'generate-image-gpt-2');
    const placeholderAspectRatio = computed(() => state.selectedSize.replace('x', ' / '));

    const props = defineProps<{
        images: {
            show: any;
            imageElement: any;
            clickImageAfterInsert: any;
        };
        viewModel: any;
    }>();

    onMounted(async () => {
        await initHistory();

        props.images.show &&
            props.images.show.subscribe((newValue: boolean) => {
                shared.show = newValue;
                if (newValue === true) init();
            });
    });

    const state = reactive<{
        commandText: string;
        exampleCommandText: string;
        imageCategories: AiImageCategory[];
        exampleImagePrompts: AiDropdownOption[];
        selectedQuality: 'auto' | 'high' | 'medium' | 'low';
        selectedSize: '1024x1024' | '1024x1536' | '1536x1024';
    }>({
        commandText: '',
        exampleCommandText: '',
        imageCategories: [],
        exampleImagePrompts: [],
        selectedQuality: 'auto',
        selectedSize: '1536x1024',
    });

    const commandText = (): string => {
        let text = state.commandText;

        state.imageCategories.forEach((category) => {
            category.SelectedOptions.forEach((option) => {
                text = text + ', ' + option;
            });
        });

        return text.substring(0, 999);
    };

    const canSave = (): boolean => {
        if (disabled()) return false;

        return shared.resultOptions && shared.resultOptions.length > 0;
    };

    const init = async () => {
        shared.addResults = true;
        shared.results = [];
        shared.resultOptions = [];
        shared.current = null;

        historyBaseSet.clear();
        history.value.forEach((e) => historyBaseSet.add(e.base64));
        shared.resultOptions.push(...history.value.map((e) => e.base64));

        await updateConsumption();

        if (state.imageCategories.length == 0) {
            state.imageCategories = getImageCategories();

            state.exampleCommandText = getSentencePromptExample();
            state.commandText = state.exampleCommandText;
        }
    };

    const save = async (insert: boolean, imageData: string) => {
        if (shared.loading) return;

        shared.loading = true;

        const fileName = 'Images/ai-generated-image-' + Date.now() + '.png';

        await uploadImageBase64(fileName, imageData, 'AI generated')
            .then((responses: UploadedResourceResponse[]) => {
                if (insert) {
                    const response = responses[0];
                    const imageElement = props.images.imageElement().$imageEl;

                    imageInsertIntoElement(response.Url, imageElement);

                    props.images.show(false);
                }

                props.viewModel().loadAccountImages();
            })
            .catch(() => {
                handleUnsuccessfulResponse(null);
            })
            .finally(() => {
                shared.loading = false;
            });
    };

    const imageInsertIntoElement = (url: string, imageEl: any) => {
        if (imageEl === null) return;

        imageEl.attr('src', url).change();

        if (props.images.clickImageAfterInsert() == true) {
            imageEl.click();
        }
    };

    const clear = () => {
        state.commandText = '';

        state.imageCategories.forEach((category) => {
            category.SelectedOptions.splice(0, category.SelectedOptions.length);
        });
    };

    const randomizePrompt = () => {
        const generationExample = getGenerationExample();

        state.commandText = generationExample.Text;
    };

    const randomizeCategories = () => {
        const generationExample = getGenerationExample();

        state.imageCategories.forEach((category) => {
            const exampleCategory = generationExample.Categories.find((c) => c.Text == category.Text);

            category.SelectedOptions.splice(0, category.SelectedOptions.length);
            Object.assign(category.SelectedOptions, exampleCategory?.SelectedOptions ?? []);
        });
    };

    const randomlyGenerateImage = () => {
        randomizePrompt();

        randomizeCategories();

        generateImage();
    };

    const generateImage = () => {
        const parameters: Record<string, string> = {
            quality: state.selectedQuality,
            size: state.selectedSize,
        };

        const prompt = commandText();
        const prevLength = shared.resultOptions.length;
        const categorySelections: Record<string, string[]> = {};
        state.imageCategories.forEach((category) => {
            categorySelections[category.Text] = [...category.SelectedOptions];
        });

        makeOptionsRequest('generate-image-gpt-2', prompt, parameters, async () => {
            const newlyGenerated = shared.resultOptions.slice(prevLength);
            for (const base64 of newlyGenerated) {
                await addToHistory({
                    base64,
                    prompt,
                    size: state.selectedSize,
                    quality: state.selectedQuality,
                    categorySelections,
                });
            }

            clear();
        });
    };

    const deleteImage = async (base64: string) => {
        const entry = history.value.find((e) => e.base64 === base64);
        if (entry) await removeFromHistory(entry.id);
        const idx = shared.resultOptions.indexOf(base64);
        if (idx !== -1) shared.resultOptions.splice(idx, 1);
    };

    const restorePrompt = (base64: string) => {
        const entry = history.value.find((e) => e.base64 === base64);
        if (!entry) return;
        state.commandText = entry.prompt;
        state.selectedSize = entry.size as typeof state.selectedSize;
        state.selectedQuality = entry.quality as typeof state.selectedQuality;
        if (entry.categorySelections) {
            state.imageCategories.forEach((category) => {
                const saved = entry.categorySelections![category.Text];
                category.SelectedOptions.splice(0, category.SelectedOptions.length, ...(saved ?? []));
            });
        }
    };
</script>

<template>
    <Modal
        :title="$Resources.AiAssistantCreateSurveyTitle"
        :beta="false"
        icon="ai"
        :show="shared.show"
        size="lg"
        @close="props.images.show(false)"
        :scrollBody="false">
        <template #content>
            <div class="ai-images">
                <div class="d-flex flex-wrap align-items-baseline m-sm-b" v-if="shared.termsAccepted && historyImages.length > 0">
                    <button type="button" class="btn btn-default btn-sm m-auto-l" :aria-expanded="historyOpen" @click="historyOpen = !historyOpen">
                        {{ $Resources.HistoryLabel }} ({{ historyImages.length }})
                    </button>
                </div>

                <Terms :termsAccepted="shared.termsAccepted" @accept="acceptTerms()"></Terms>

                <template v-if="shared.termsAccepted">
                    <div v-if="historyImages.length > 0">
                        <Collapse :open="historyOpen">
                            <div class="m-lg-b" v-for="imageData in historyImages">
                                <img class="img-responsive" :src="imageData" :alt="$Resources.AIAssistantImage" />

                                <div class="d-flex flex-wrap gap-xs justify-content-end form-group m-sm-t">
                                    <button type="button" class="btn btn-default btn-sm" @click="deleteImage(imageData)" :disabled="shared.loading">
                                        {{ $Resources.RemoveButton }}
                                    </button>

                                    <button type="button" class="btn btn-default btn-sm" @click="restorePrompt(imageData)" :disabled="shared.loading">
                                        {{ $Resources.AiEditorRestorePrompt }}
                                    </button>

                                    <button type="button" class="btn btn-default btn-sm" @click="save(false, imageData)" :disabled="!canSave()">
                                        {{ $Resources.AIAssistantSaveImage }}
                                    </button>

                                    <button type="button" class="btn btn-primary btn-sm" @click="save(true, imageData)" :disabled="!canSave()">
                                        {{ $Resources.AIAssistantInsertImage }}
                                    </button>
                                </div>
                            </div>
                        </Collapse>
                    </div>

                    <div v-if="isGenerating" class="m-lg-b">
                        <div class="ai-image-placeholder ai-image-generating p-relative" :style="{ aspectRatio: placeholderAspectRatio }"></div>
                    </div>

                    <div v-show="newImages.length > 0">
                        <div class="m-lg-b" v-for="imageData in newImages">
                            <img class="img-responsive" :src="imageData" :alt="$Resources.AIAssistantImage" />

                            <div class="d-flex flex-wrap gap-xs justify-content-end form-group m-sm-t">
                                <button type="button" class="btn btn-default btn-sm" @click="deleteImage(imageData)" :disabled="shared.loading">
                                    {{ $Resources.RemoveButton }}
                                </button>

                                <button type="button" class="btn btn-default btn-sm" @click="restorePrompt(imageData)" :disabled="shared.loading">
                                    {{ $Resources.AiEditorRestorePrompt }}
                                </button>

                                <button type="button" class="btn btn-default btn-sm" @click="save(false, imageData)" :disabled="!canSave()">
                                    {{ $Resources.AIAssistantSaveImage }}
                                </button>

                                <button type="button" class="btn btn-primary btn-sm" @click="save(true, imageData)" :disabled="!canSave()">
                                    {{ $Resources.AIAssistantInsertImage }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="form-group m-sm-b">
                        <FormTextArea
                            :label="$Resources.AIAssistantPrompt"
                            :infoText="$Resources.AIAssistantPromptInfoText"
                            :placeholder="state.exampleCommandText"
                            v-model="state.commandText"
                            :maxlength="500"
                            :disabled="shared.loading"
                            :rows="2" />
                    </div>

                    <div class="form-group">
                        <button type="button" class="btn btn-default btn-sm" @click="randomizePrompt()" :disabled="disabled()">
                            {{ $Resources.AIAssistantRandomizePrompt }}
                        </button>
                    </div>

                    <div class="row">
                        <div class="col-sm-6">
                            <FormSelect
                                class="form-group"
                                :label="$Resources.AIAssistantSize"
                                :options="sizeOptions"
                                v-model="state.selectedSize"
                                :disabled="shared.loading" />
                        </div>
                        <div class="col-sm-6">
                            <FormSelect
                                class="form-group"
                                :label="$Resources.AIAssistantQuality"
                                :options="qualityOptions"
                                :infoText="$Resources.AIAssistantQualityInfoBody"
                                v-model="state.selectedQuality"
                                :disabled="shared.loading" />
                        </div>
                    </div>

                    <h4>{{ $Resources.AIAssistantCategories }}</h4>
                    <div class="row">
                        <div class="col-sm-6" v-for="category in state.imageCategories">
                            <MultiSelect
                                class="form-group m-sm-b"
                                :label="category.Text"
                                :items="category.Options"
                                v-model:selectedItems="category.SelectedOptions"
                                selectProp="Text"
                                displayProp="Text"
                                selectOnly="Text"
                                :disabled="shared.loading" />
                        </div>
                    </div>

                    <div class="form-group">
                        <button type="button" class="btn btn-default btn-sm" @click="randomizeCategories()" :disabled="disabled()">
                            {{ $Resources.AIAssistantRandomizeCategories }}
                        </button>
                    </div>

                    <template v-if="showConsumption()">
                        <p>{{ getFormattedConsumption() }} % {{ $Resources.AIAssistantConsumptionPostText }}</p>
                    </template>

                    <div class="form-group d-flex flex-wrap gap-sm">
                        <button type="button" class="btn btn-subtle m-auto-r" @click="clear()" :disabled="shared.loading">
                            {{ $Resources.AIAssistantClear }}
                        </button>

                        <button type="button" class="btn btn-subtle" @click="randomlyGenerateImage()" :disabled="disabled()">
                            {{ $Resources.AIAssistantRandomlyGenerateImage }}
                        </button>

                        <button type="button" class="btn btn-primary" :class="shared.loading && 'is-busy'" @click="generateImage()" :disabled="disabled()">
                            {{ $Resources.AIAssistantGenerateImage }}
                        </button>
                    </div>
                </template>

                <Loading :show="shared.loading" :fixed="true"></Loading>
            </div>
        </template>
    </Modal>
</template>
